// src/components/ui/Bulle.tsx — SCE Issues
// Bulle de choix : famille, criticité, motif d'annulation, badge de clôture.
//
// ─── Pourquoi des bulles (Lot 3 bis, maquette validée le 24/09/2026) ───
//
// Les choix de la fiche problème étaient des listes déroulantes, des grilles
// de boutons de largeur égale ou des lignes pleine largeur. Chacune prenait
// une ligne, ou cachait ses options derrière un toucher. Les bulles
// s'enchaînent sur la ligne : on voit toutes les options d'un coup d'œil et
// on choisit d'un seul toucher.
//
// ─── Ce qui ne bouge pas ───
//
// 44 px de haut minimum : la cible de doigt de l'opérateur debout, mains
// potentiellement occupées. Le gain de place vient de l'enchaînement sur la
// ligne, jamais d'une cible rétrécie. Un libellé long (« Raccordement —
// inversion de fils / mauvais câblage ») passe à la ligne DANS sa bulle
// plutôt que d'en déborder.
//
// Sélection : jaune d'interaction + contour noir, comme les puces de filtre de
// la liste. Une couleur de sélection propre (criticité) peut la remplacer.

import type { CSSProperties } from 'react'

export interface CouleurBulle {
  fond: string
  texte: string
  bord: string
}

const SELECTION_PAR_DEFAUT: CouleurBulle = {
  fond: 'var(--color-yellow)',
  texte: 'var(--color-black)',
  bord: 'var(--color-black)',
}

export interface BulleProps {
  libelle: string
  selectionne: boolean
  onClick: () => void
  /**
   * `radio` pour un choix unique (famille, criticité, motif), `checkbox` pour
   * un choix multiple (badges). Change ce qu'annonce un lecteur d'écran.
   */
  role: 'radio' | 'checkbox'
  desactive?: boolean
  /** Couleurs à l'état sélectionné — défaut : jaune d'interaction. */
  couleurSelection?: CouleurBulle
  /**
   * Pastille de couleur devant le libellé. Sert la criticité : la couleur
   * Traffic Light se lit AVANT la sélection, pas seulement après.
   */
  pastille?: string
}

export function Bulle({
  libelle,
  selectionne,
  onClick,
  role,
  desactive,
  couleurSelection = SELECTION_PAR_DEFAUT,
  pastille,
}: BulleProps): JSX.Element {
  const style: CSSProperties = {
    minHeight: 44,
    boxSizing: 'border-box',
    padding: '10px 14px',
    borderRadius: 22,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    textAlign: 'left',
    fontSize: 'var(--font-size-copy2)',
    lineHeight: '18px',
    fontWeight: selectionne ? 600 : 400,
    backgroundColor: selectionne ? couleurSelection.fond : 'var(--color-white)',
    color: selectionne ? couleurSelection.texte : 'var(--color-black)',
    border: `1px solid ${selectionne ? couleurSelection.bord : 'var(--color-border)'}`,
    opacity: desactive ? 0.5 : 1,
    cursor: desactive ? 'default' : 'pointer',
  }

  return (
    <button
      type="button"
      role={role}
      aria-checked={selectionne}
      disabled={desactive}
      onClick={onClick}
      style={style}
    >
      {pastille && (
        <span
          aria-hidden="true"
          style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            flexShrink: 0,
            // Sélectionnée, la bulle prend la couleur de la pastille : on la
            // passe alors dans la couleur du texte pour qu'elle reste visible.
            backgroundColor: selectionne ? couleurSelection.texte : pastille,
          }}
        />
      )}
      {libelle}
    </button>
  )
}

/** Conteneur d'un groupe de bulles : retour à la ligne, espacement serré. */
export function GroupeBulles({
  libelle,
  multiple,
  children,
}: {
  /** Nom du groupe pour les lecteurs d'écran (« Famille », « Criticité »…). */
  libelle: string
  multiple?: boolean
  children: React.ReactNode
}): JSX.Element {
  return (
    <div
      role={multiple ? 'group' : 'radiogroup'}
      aria-label={libelle}
      style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
    >
      {children}
    </div>
  )
}
